import { IAssemblyApplicationTypeResponse } from '@kcftech/prometheus-client';
import { DataSourceTable } from 'components/data_source_table/DataSourceTable';
import { TableColumn } from 'components/data_table/types';
import { ArrowLinkEditorRenderer } from 'components/link/ArrowLink';
import { AssemblyApplicationTypePagedDataSource } from 'custom_hooks/data_hooks/data_source/assemblyApplicationTypesDataSource';
import { AssemblyTypeNameInlineEditor } from 'pages/Settings/AssemblyType/table_cells/AssemblyTypeNameInlineEditor';
import { AssemblyTypeStatusInlineEditor } from 'pages/Settings/AssemblyType/table_cells/AssemblyTypeStatusInlineEditor';
import { useMemo } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { DebugValue } from 'shared/debug/DebugValue';
import { getId } from 'utils/data';
import { appendToRoute } from 'utils/RouteHelper';

export type AssemblyTypesTableProps = Readonly<{
    className?: string;
    dataSource: AssemblyApplicationTypePagedDataSource;
}>;

export function AssemblyTypesTable({ dataSource, className }: AssemblyTypesTableProps) {
    const { url } = useRouteMatch();
    const { reloadPage } = dataSource;

    const columns = useMemo<TableColumn<IAssemblyApplicationTypeResponse>[]>(
        () => [
            {
                header: () => 'Name',
                id: 'Name',
                value: ({ rowData }) => (
                    <>
                        <DebugValue value={rowData} description="Assembly Type" />
                        <AssemblyTypeNameInlineEditor
                            assemblyType={rowData}
                            onUpdate={reloadPage}
                            renderValue={(value) => (
                                <ArrowLinkEditorRenderer
                                    value={value}
                                    to={appendToRoute(url, rowData.Id)}
                                    title="Select assembly type"
                                    aria-label={`${rowData.Name} assembly type name arrow link`}
                                />
                            )}
                        />
                    </>
                ),
                sortOn: 'Name',
                primary: true,
                locked: true,
            },
            {
                header: () => 'Code',
                id: 'Code',
                value: ({ rowData }) => <div className="text-nowrap">{rowData.Code}</div>,
                sortOn: 'Code',
                locked: true,
            },
            {
                header: () => 'Status',
                id: 'Status',
                value: ({ rowData }) => (
                    <div className="text-nowrap">
                        <AssemblyTypeStatusInlineEditor
                            assemblyType={rowData}
                            onUpdate={reloadPage}
                        />
                    </div>
                ),
                locked: true,
            },
        ],
        [reloadPage, url],
    );

    return (
        <DataSourceTable
            dataSource={dataSource}
            rowKey={getId}
            columns={columns}
            noResultMessage="No assembly types found"
            tableClassName={className}
        />
    );
}
